"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var nativeApi_1 = require("./utils/nativeApi");
var consts_1 = require("./utils/consts");
var render_1 = require("./render/render");
var queuedRender_1 = require("./render/queuedRender");
var nodesToSlots_1 = require("./utils/nodesToSlots");
var arrayToNodeList_1 = require("./utils/arrayToNodeList");
var adoption_1 = require("./utils/adoption");
var renderRoot_1 = require("./utils/renderRoot");
var domWalks_1 = require("./utils/domWalks");
var patchSlot_1 = require("./utils/patchSlot");
var uniq_1 = require("./utils/uniq");
var lifecycleElement_1 = require("./lifecycleElement");
/**
 * The base class for custom elements, with support of jsx and server side rendering and content disribution
 */
var CustomElement = /** @class */ (function (_super) {
    tslib_1.__extends(CustomElement, _super);
    function CustomElement() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /** @internal set to true while rendering */
        _this._rendering = false;
        // native api childNodes (for domino)
        _this.nativeNodesArray = arrayToNodeList_1.arrayToNodeList([]);
        // faked child nodes which keeps slot
        _this.childNodesArray = arrayToNodeList_1.arrayToNodeList([]);
        return _this;
    }
    /** @internal will be called from queuedRender if renderCb returns something */
    CustomElement.prototype.render = function (content) {
        this.prepareRenderRoot();
        var before = nativeApi_1.isNativeApiExposed();
        nativeApi_1.exposeNativeApi(true);
        if (!this.slots) {
            this.slots = nodesToSlots_1.nodesToSlots(this.childNodesArray);
        }
        // patch slots if they were prerendered
        patchSlot_1.patchSlots(this.shadowRoot);
        this._rendering = true;
        render_1.render(this.renderTarget, content, {
            slots: this.slots,
            component: this
        });
        this._rendering = false;
        nativeApi_1.exposeNativeApi(before);
        // patch slots if they have appeared after rendering
        patchSlot_1.patchSlots(this.shadowRoot);
    };
    /**
     * Run render unconditionally
     */
    CustomElement.prototype.rendererCallback = function () {
        if (!this.preventRender) {
            // putting rendering in queue
            queuedRender_1.queuedRender(this);
        }
    };
    /**
     * Return jsx to render it as element's markup
     * Return null for not to change existig DOM
     * Return boolean to remove element's markup
     */
    CustomElement.prototype.renderCallback = function () {
        // this is called from queued render
    };
    /**
     * Called immediately after `renderCallback`
     */
    CustomElement.prototype.renderedCallback = function () {
        // this is called from queued render
    };
    /**
     * Called on children changed.
     */
    CustomElement.prototype.childrenChangedCallback = function () {
        // invalidate slots
        this.slots = null;
        // try rendering
        this.rendererCallback();
    };
    // override native HTML properties for have shadowDom
    CustomElement.prototype.contains = function (child) {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.contains.call(this, child);
        }
        else {
            return this === child ||
                this.childNodesArray.indexOf(child) > -1 ||
                this.childNodesArray.some(function (node) { return node.contains(child); });
        }
    };
    CustomElement.prototype.hasChildNodes = function () {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.hasChildNodes.call(this);
        }
        else {
            return !!this.childNodesArray.length;
        }
    };
    Object.defineProperty(CustomElement.prototype, "shadowRoot", {
        get: function () {
            return this.renderTarget;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "__CE_shadowRoot", {
        // ELEMENTS-406 is needed for @webcomponents/custom-element
        // to traverse trees correctly
        get: function () {
            return this.shadowRoot;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "firstChild", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.firstChildNative.get.call(this);
            }
            else {
                if (this.childNodesArray.length > 0) {
                    return this.childNodesArray[0];
                }
                else {
                    return null;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "lastChild", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.lastChildNative.get.call(this);
            }
            else {
                if (this.childNodesArray.length > 0) {
                    return this.childNodesArray[this.childNodesArray.length - 1];
                }
                else {
                    return null;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "firstElementChild", {
        get: function () {
            if (this.children.length > 0) {
                return this.children[0];
            }
            else {
                return null;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "lastElementChild", {
        get: function () {
            var children = this.children;
            if (children.length > 0) {
                return children[children.length - 1];
            }
            else {
                return null;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "innerHTML", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.innerHTMLNative.get.call(this);
            }
            else {
                return this.childNodesArray.reduce(function (content, node) {
                    if (node instanceof Element) {
                        return content + node.outerHTML;
                    }
                    else if (node.nodeType === 8) {
                        return content + ("<!--" + node.data + "-->");
                    }
                    else if (typeof node.data === 'string') {
                        return content + node.data;
                    }
                }, '');
            }
        },
        set: function (value) {
            var _this = this;
            if (nativeApi_1.isNativeApiExposed()) {
                nativeApi_1.innerHTMLNative.set.call(this, value);
                // TODO Throw error if firstchild is not a render-root, take the render-root, and retake the slots from it
            }
            else {
                // stop rendering during current changes
                var previousPreventRender = this.preventRender;
                this.preventRender = true;
                // using fake element to apply innerHTML
                var fakeParent = this.ownerDocument.createElement('div');
                fakeParent.innerHTML = value;
                // removing old children
                while (this.childNodesArray.length > 0) {
                    this.removeChild(this.childNodesArray[0]);
                }
                // adopt children
                Array.prototype.slice.call(fakeParent.childNodes || []).forEach(function (node) {
                    _this.appendChild(node);
                });
                this.preventRender = previousPreventRender;
                // rendering
                this.childrenChangedCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "outerHTML", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.outerHTMLNative.get.call(this);
            }
            else {
                var attrsAsString = '';
                for (var i = 0; i < this.attributes.length; i++) {
                    var a = this.attributes.item(i);
                    attrsAsString += " " + a.name + "=\"" + a.value + "\"";
                }
                return "<" + this.localName + attrsAsString + ">" + this.innerHTML + "</" + this.localName + ">";
            }
        },
        set: function (value) {
            // TODO Probably we should throw an error
            /* istanbul ignore next - no domino implementation */
            nativeApi_1.outerHTMLNative.set.call(this, value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "textContent", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.textContentNative.get.call(this);
            }
            else {
                return this.childNodesArray.reduce(function (content, child) {
                    return content + child.textContent;
                }, '');
            }
        },
        set: function (value) {
            if (nativeApi_1.isNativeApiExposed()) {
                // TODO Probably we should throw an error
                nativeApi_1.textContentNative.set(value);
            }
            else {
                // using fake element to apply textContent
                var fakeParent = this.ownerDocument.createElement('div');
                fakeParent.textContent = value;
                // moving content from fakeParent to this by innerHTML
                this.innerHTML = fakeParent.innerHTML;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "children", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                return nativeApi_1.childrenNative.get.call(this);
            }
            else {
                return arrayToNodeList_1.arrayToNodeList(this.childNodesArray.filter(function (n) { return n.nodeType === 1; }));
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "childNodes", {
        get: function () {
            if (nativeApi_1.isNativeApiExposed()) {
                /* istanbul ignore if - no domino implementation */
                if (nativeApi_1.childNodesNative) {
                    return nativeApi_1.childNodesNative.get.call(this);
                }
                else {
                    return this.nativeNodesArray;
                }
            }
            else {
                return this.childNodesArray;
            }
        },
        set: function (v) {
            if (nativeApi_1.isNativeApiExposed()) {
                /* istanbul ignore if - no domino implementation */
                if (nativeApi_1.childNodesNative) {
                    nativeApi_1.childNodesNative.set(v);
                }
                else {
                    this.nativeNodesArray = v;
                }
                // TODO Throw error if more then one child and firstchild is not a render-root, take the render-root, and retake the slots from it
            }
            else {
                this.childNodesArray = v;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CustomElement.prototype, "childElementCount", {
        get: function () {
            return this.children.length;
        },
        enumerable: true,
        configurable: true
    });
    CustomElement.prototype._appendChild = function (newChild) {
        // ts doesn't know domino has this method in super
        var super_ = Element.prototype;
        super_._appendChild.call(this, newChild);
        if (!nativeApi_1.isNativeApiExposed()) {
            this.childNodesArray.push(newChild);
            adoption_1.adoptChildNode(this, newChild);
        }
        return newChild;
    };
    CustomElement.prototype.appendChild = function (newChild) {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.appendChild.call(this, newChild);
        }
        else {
            // pushing to the array of items
            uniq_1.uniqPush(this.childNodesArray, newChild);
            // bind parent to item
            adoption_1.adoptChildNode(this, newChild);
            // rendering
            this.childrenChangedCallback();
            return newChild;
        }
    };
    CustomElement.prototype.insertBefore = function (newChild, refChild) {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.insertBefore.call(this, newChild, refChild);
        }
        else {
            if (!refChild) {
                uniq_1.uniqPush(this.childNodesArray, newChild);
            }
            else {
                // inserting new child in to children
                var i = this.childNodesArray.indexOf(refChild);
                if (i > -1) {
                    uniq_1.uniqInsert(this.childNodesArray, i, newChild);
                }
                else {
                    uniq_1.uniqPush(this.childNodesArray, newChild);
                }
            }
            // bind parent to item
            adoption_1.adoptChildNode(this, newChild);
            // rendering
            this.childrenChangedCallback();
            return newChild;
        }
    };
    CustomElement.prototype.removeChild = function (oldChild) {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.removeChild.call(this, oldChild);
        }
        else {
            // removing item from array
            var i = this.childNodesArray.indexOf(oldChild);
            if (i > -1) {
                this.childNodesArray.splice(i, 1);
                adoption_1.unadoptChildNode(this, oldChild);
                // if child was inserted into element when its parent was null and element had no slots
                // it doesn't have original parent node
                if (oldChild.parentNode) {
                    oldChild.parentNode.removeChild(oldChild);
                }
            }
            else {
                // when domino parses HTML (document.innerHTML = <x-ce/>) it arrange nodes in the mock DOM
                // in this case CE is the original parent of the oldChild
                _super.prototype.removeChild.call(this, oldChild);
            }
            // rendering
            this.childrenChangedCallback();
            return oldChild;
        }
    };
    CustomElement.prototype.replaceChild = function (newChild, oldChild) {
        if (nativeApi_1.isNativeApiExposed()) {
            return _super.prototype.replaceChild.call(this, newChild, oldChild);
        }
        else {
            // looking for old child
            var i = this.childNodesArray.indexOf(oldChild);
            if (i > -1) {
                // stop rendering during current changes
                var previousPreventRender = this.preventRender;
                this.preventRender = true;
                // removing item
                this.removeChild(oldChild);
                // adding item
                this.insertBefore(newChild, this.childNodesArray[i]);
                this.preventRender = previousPreventRender;
                // rendering
                this.childrenChangedCallback();
            }
            return oldChild;
        }
    };
    CustomElement.prototype.prepareRenderRoot = function () {
        var _this = this;
        if (!this.renderTarget) {
            nativeApi_1.exposeNativeApi(true);
            /* istanbul ignore else - no domino implementation */
            if (this.childNodesArray.length > 0) {
                // for prerendered on domino
                var firstChild = this.childNodesArray[0];
                if (firstChild instanceof Element && firstChild.tagName.toLowerCase() === consts_1.RENDER_TARGET_ELEMENT_TAG_NAME) {
                    // render target found
                    this.renderTarget = this.childNodesArray.shift();
                    this.nativeNodesArray.push(this.renderTarget);
                }
                else {
                    // if there is no render target but we have children
                    // we have to adopt them
                    this.childNodesArray.forEach(function (e) {
                        if (e.parentNode === _this) {
                            e.parentNode = _this;
                            _this.nativeNodesArray.push(e);
                            _this.removeChild(e);
                        }
                        adoption_1.adoptChildNode(_this, e);
                    });
                }
            }
            else {
                if (this.childNodes.length > 0) {
                    // for prerendered on browsers
                    var firstChild = this.childNodes[0];
                    // looking for render target
                    if (firstChild instanceof Element && firstChild.tagName.toLowerCase() === consts_1.RENDER_TARGET_ELEMENT_TAG_NAME) {
                        // render target found
                        this.renderTarget = this.childNodes[0];
                        this.nativeNodesArray.push(this.renderTarget);
                    }
                    else {
                        // if there is no render target but we have children
                        // we have to adopt them
                        Array.prototype.forEach.call(this.childNodes, function (e) {
                            _this.childNodesArray.push(e);
                            adoption_1.adoptChildNode(_this, e);
                        });
                        // children will be inserted to the element only if it has slots
                        this.childNodesArray.forEach(function (e) { return _this.removeChild(e); });
                    }
                }
            }
            if (this.renderTarget) {
                renderRoot_1.adoptRenderRoot(this.renderTarget);
                // if we have render target we will look for slots
                var slots = domWalks_1.findSlots(this.renderTarget);
                // all slots elements we will push to fake childNodes
                slots.forEach(function (slot) {
                    // if content within named slot has no 'slot' attriubte, then it is default content
                    // it should not be inside childNodesArray
                    if (slot.getAttribute('name') && slot.firstChild &&
                        (slot.firstChild.nodeType !== 1 ||
                            !slot.firstElementChild.getAttribute('slot'))) {
                        return;
                    }
                    Array.prototype.forEach.call(slot.childNodes, function (child) {
                        _this.childNodesArray.push(child);
                        adoption_1.adoptChildNode(_this, child);
                    });
                });
            }
            else {
                this.renderTarget = renderRoot_1.createRenderRoot(this.ownerDocument);
                this.appendChild(this.renderTarget);
            }
            nativeApi_1.exposeNativeApi(false);
            // if we have child nodes we will notify the callback but without render
            if (this.childNodes.length > 0) {
                this.preventRender = true;
                this.childrenChangedCallback();
                this.preventRender = false;
            }
        }
    };
    return CustomElement;
}(lifecycleElement_1.LifecycleElement));
exports.CustomElement = CustomElement;
